import { Link } from "react-router-dom";
import {
  Layers,
  Zap,
  BadgeCheck,
  LayoutDashboard,
  Info,
  Handshake,
  Briefcase,
  Mail,
  FileText,
  Shield,
  FileSearch,
  AlertCircle,
  MessageCircle,
  LifeBuoy,
  UserPlus,
  Activity,
} from "lucide-react";

/**
 * Premium marketing footer — 4 columns (Product / Company / Legal /
 * Support). Every link is a real route with an icon and a short
 * `title` tooltip on hover.
 */
const COL = {
  Product: [
    { icon: Layers, label: "Cover tiers", to: "/cover-tiers", desc: "Starter, Growth, Business cover — from KES 99 / week." },
    { icon: Zap, label: "FlashClaim", to: "/flashclaim", desc: "Instant claim reporting on WhatsApp. Paid in seconds." },
    { icon: BadgeCheck, label: "Trust Badge", to: "/trust-badge", desc: "Fortiqa Verified Merchant Badge — live-verifying." },
    { icon: LayoutDashboard, label: "Dashboard preview", to: "/dashboard-preview", desc: "Cover, claims, CyberScore, payments, alerts." },
  ],
  Company: [
    { icon: Info, label: "About", to: "/about", desc: "Cyber cover for African merchants — Kenya-first." },
    { icon: Handshake, label: "Partners", to: "/partners", desc: "Banks, PSPs, SACCOs, POS networks, brokers." },
    { icon: Briefcase, label: "Careers", to: "/careers", desc: "Fraud ops, claims, engineering, partnerships, compliance." },
    { icon: Mail, label: "Contact", to: "/contact", desc: "WhatsApp, email, phone, or contact form." },
  ],
  Legal: [
    { icon: FileText, label: "Terms", to: "/terms", desc: "Platform terms, eligibility, partner underwriting." },
    { icon: Shield, label: "Privacy", to: "/privacy", desc: "What data we collect, how we use it, your rights." },
    { icon: FileSearch, label: "Policy documents", to: "/policy-documents", desc: "KFS, policy wording, claims guide, privacy notice." },
    { icon: AlertCircle, label: "Complaints", to: "/complaints", desc: "Four-step process. Response within 5 business days." },
  ],
  Support: [
    { icon: MessageCircle, label: "WhatsApp support", to: "/whatsapp-support", desc: "24/7 for claims, business hours for everything else." },
    { icon: LifeBuoy, label: "Claims help", to: "/claims-help", desc: "Evidence checklist + what we accept." },
    { icon: UserPlus, label: "Merchant onboarding", to: "/merchant-onboarding", desc: "Five steps from signup to active cover." },
    { icon: Activity, label: "Status", to: "/status", desc: "Live service status — site, dashboard, claims, payments." },
  ],
};

function ColumnHeading({ children }) {
  return (
    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#114232] mb-4">
      {children}
    </div>
  );
}

function FooterLink({ item, testid }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      title={item.desc}
      className="group flex items-start gap-2.5 py-2 text-sm text-[#5c6360] hover:text-[#114232] focus:outline-none focus:text-[#114232] transition-colors"
      data-testid={testid}
    >
      <span className="w-7 h-7 rounded-lg bg-[#114232]/6 grid place-items-center shrink-0 mt-0.5 group-hover:bg-[#114232]/12 transition-colors">
        <Icon className="w-3.5 h-3.5 text-[#114232]" strokeWidth={1.8} />
      </span>
      <span className="flex-1">
        <span className="block font-semibold leading-tight">{item.label}</span>
        <span className="block text-[11px] text-[#5c6360]/80 mt-0.5 leading-snug">
          {item.desc}
        </span>
      </span>
    </Link>
  );
}

export default function MarketingFooter() {
  return (
    <footer
      data-testid="marketing-footer"
      className="bg-[#fbf9f1] border-t border-[#114232]/10"
    >
      <div className="px-6 lg:px-10 max-w-7xl mx-auto py-14 lg:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand column */}
          <div className="lg:col-span-1 space-y-5">
            <Link to="/" className="flex items-center gap-2.5" data-testid="footer-brand">
              <img src="/fortiqa-mark.svg" alt="Fortiqa" className="w-9 h-9 object-contain" />
              <span className="font-heading text-lg font-bold tracking-tight text-[#114232]">
                Fortiqa
              </span>
            </Link>
            <p className="text-sm text-[#5c6360] leading-relaxed max-w-xs">
              Cyber cover and merchant protection for African businesses. Embedded, weekly,
              paid in seconds.
            </p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-[#5c6360]">
                <span className="w-2 h-2 rounded-full bg-[#2d7a4a] animate-pulse" />
                All systems operational
              </div>
              <a
                href="https://wa.me/254700000000?text=Hi%20Fortiqa"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-[#5c6360] hover:text-[#114232] transition-colors"
                data-testid="footer-mini-whatsapp"
              >
                <MessageCircle className="w-3 h-3" /> +254 700 000 000
              </a>
              <a
                href="mailto:hello@fortiqa.co.ke"
                className="inline-flex items-center gap-1.5 text-xs text-[#5c6360] hover:text-[#114232] transition-colors"
                data-testid="footer-mini-email"
              >
                <Mail className="w-3 h-3" /> hello@fortiqa.co.ke
              </a>
            </div>
          </div>

          {Object.entries(COL).map(([heading, items]) => (
            <div key={heading}>
              <ColumnHeading>{heading}</ColumnHeading>
              <div>
                {items.map((it) => (
                  <FooterLink
                    key={it.label}
                    item={it}
                    testid={`footer-link-${heading.toLowerCase()}-${it.label
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 pt-8 border-t border-[#114232]/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-xs text-[#5c6360] font-mono" data-testid="footer-tagline">
            © {new Date().getFullYear()} Fortiqa · Built for IRA Kenya regulatory alignment
          </div>
          <div className="text-xs text-[#5c6360]">Cyber Cover. Business Confidence.</div>
        </div>
      </div>
    </footer>
  );
}
